import type { TextFilterGridLoadParams, TextFilterGridLoadResult } from "./textFilterGridTypes.ts";

export const TEXT_FILTER_GRID_CACHE_TTL_MS = 30 * 60 * 1000;
const CACHE_MAX_ENTRIES = 50;

interface BoundedTtlCacheEntry<T> {
  value: T;
  expiry: number;
}

export class BoundedTtlCache<T> {
  private readonly entries = new Map<string, BoundedTtlCacheEntry<T>>();

  constructor(
    private readonly ttl: number = TEXT_FILTER_GRID_CACHE_TTL_MS,
    private readonly maxEntries: number = CACHE_MAX_ENTRIES
  ) {}

  public set(key: string, value: T): void {
    this.entries.delete(key);
    if (this.entries.size >= this.maxEntries) {
      const oldestKey = this.entries.keys().next().value;
      if (oldestKey !== undefined) {
        this.entries.delete(oldestKey);
      }
    }
    this.entries.set(key, { value, expiry: Date.now() + this.ttl });
  }

  public get(key: string): T | undefined {
    const entry = this.entries.get(key);
    if (entry === undefined) {
      return undefined;
    }
    if (Date.now() > entry.expiry) {
      this.entries.delete(key);
      return undefined;
    }
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }

  public clear(): void {
    this.entries.clear();
  }
}

interface TextFilterGridRowsCache {
  get: (key: string) => TextFilterGridLoadResult | undefined;
  set: (key: string, value: TextFilterGridLoadResult) => void;
  clear: () => void;
}

export interface TextFilterGridCacheRegistry {
  get: (scopeKey: string) => TextFilterGridRowsCache;
  clear: () => void;
}

interface CacheEntry {
  cache: TextFilterGridRowsCache;
  expirationTimer: ReturnType<typeof setTimeout>;
}

export interface TextFilterGridQuery {
  queryKey: string;
  persistent: boolean;
}

const getRequestKey = (queryKey: string, params: TextFilterGridLoadParams): string =>
  JSON.stringify([queryKey, params.range, params.sortModel, params.allRows === true]);

export class QueryScopedTextFilterGridCache {
  private readonly localCache: TextFilterGridRowsCache;

  constructor(
    private readonly persistentCache: TextFilterGridRowsCache,
    ttl: number = TEXT_FILTER_GRID_CACHE_TTL_MS
  ) {
    this.localCache = new BoundedTtlCache(ttl);
  }

  public set(query: TextFilterGridQuery, params: TextFilterGridLoadParams, value: TextFilterGridLoadResult): void {
    this.getCache(query).set(getRequestKey(query.queryKey, params), value);
  }

  public get(query: TextFilterGridQuery, params: TextFilterGridLoadParams): TextFilterGridLoadResult | undefined {
    return this.getCache(query).get(getRequestKey(query.queryKey, params));
  }

  private getCache(query: TextFilterGridQuery): TextFilterGridRowsCache {
    return query.persistent ? this.persistentCache : this.localCache;
  }
}

export const createTextFilterGridCacheRegistry = (
  ttl: number = TEXT_FILTER_GRID_CACHE_TTL_MS
): TextFilterGridCacheRegistry => {
  const entries = new Map<string, CacheEntry>();

  const scheduleExpiration = (key: string, cache: TextFilterGridRowsCache): ReturnType<typeof setTimeout> =>
    setTimeout(() => {
      if (entries.get(key)?.cache === cache) {
        entries.delete(key);
        cache.clear();
      }
    }, ttl);

  return {
    get: (scopeKey) => {
      const existingEntry = entries.get(scopeKey);
      if (existingEntry !== undefined) {
        clearTimeout(existingEntry.expirationTimer);
        existingEntry.expirationTimer = scheduleExpiration(scopeKey, existingEntry.cache);
        return existingEntry.cache;
      }

      const cache = new BoundedTtlCache<TextFilterGridLoadResult>(ttl);
      entries.set(scopeKey, { cache, expirationTimer: scheduleExpiration(scopeKey, cache) });
      return cache;
    },
    clear: () => {
      entries.forEach(({ cache, expirationTimer }) => {
        clearTimeout(expirationTimer);
        cache.clear();
      });
      entries.clear();
    },
  };
};

const textFilterGridCacheRegistry = createTextFilterGridCacheRegistry();

export const createQueryScopedTextFilterGridCache = (scopeKey: string): QueryScopedTextFilterGridCache =>
  new QueryScopedTextFilterGridCache(textFilterGridCacheRegistry.get(scopeKey));
